import type { ChangeEvent, FC } from 'react';
import React, { useCallback } from 'react';

import styles from './RangeSlider.module.scss';

export interface RangeSliderProps {
	className?: string;
	disableFromThumb?: boolean;
	from: number;
	fromLabel?: string;
	max: number;
	min: number;
	onChange: (from: number, to: number) => void;
	step?: number;
	to: number;
	toLabel?: string;
}

const getPercent = (value: number, min: number, max: number): number =>
	max === min ? 0 : ((value - min) / (max - min)) * 100;

export const RangeSlider: FC<RangeSliderProps> = ({
	className,
	disableFromThumb,
	from,
	fromLabel = 'Range minimum',
	max,
	min,
	onChange,
	step = 1,
	to,
	toLabel = 'Range maximum',
}) => {
	const handleFromChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = Math.min(Number(e.target.value), to - step);
			onChange(value, to);
		},
		[onChange, step, to],
	);

	const handleToChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = Math.max(Number(e.target.value), from + step);
			onChange(from, value);
		},
		[from, onChange, step],
	);

	const fromPercent = getPercent(from, min, max);
	const toPercent = getPercent(to, min, max);

	return (
		<div className={`${styles.slider} ${className ?? ''}`}>
			<div className={styles.track}>
				<div
					className={styles.range}
					style={{
						left: disableFromThumb ? '0%' : `${fromPercent}%`,
						width: `${disableFromThumb ? toPercent : toPercent - fromPercent}%`,
					}}
				/>
			</div>
			{!disableFromThumb && (
				<input
					aria-label={fromLabel}
					className={styles.thumb}
					max={max}
					min={min}
					onChange={handleFromChange}
					step={step}
					type="range"
					value={from}
				/>
			)}
			<input
				aria-label={toLabel}
				className={styles.thumb}
				max={max}
				min={min}
				onChange={handleToChange}
				step={step}
				type="range"
				value={to}
			/>
		</div>
	);
};
